import { motion } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { PLAQUETTE_IMAGE } from "@/data/content";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden pt-28 pb-20"
    >
      {/* glows: cyan + warm for a less cold feel */}
      <div className="absolute top-1/4 -left-20 w-[40rem] h-[40rem] bg-accent/15 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-10 w-[32rem] h-[32rem] bg-warm/15 blur-3xl rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center relative z-10">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono text-xs tracking-[0.25em] uppercase text-warm mb-6 flex items-center gap-3"
          >
            <span className="h-px w-8 bg-warm inline-block" />
            Agence digitale · Humain & sur mesure
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.95]"
          >
            Votre notoriété,
            <br />
            <span className="gradient-text">propulsée</span> par la tech.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed"
          >
            Chevalets avis Google connectés, menus digitaux, gestion de fiche
            Google en mode SaaS — mais humain et sur mesure. Des solutions
            modernes conçues avec nos technologies internes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group bg-accent text-background font-semibold px-7 py-3.5 flex items-center justify-center gap-2 hover:bg-accent-hover transition-colors glow-accent"
            >
              Décrocher un rendez-vous
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#solutions"
              data-testid="hero-cta-secondary"
              className="border border-border text-foreground px-7 py-3.5 text-center hover:border-warm/50 transition-colors"
            >
              Découvrir nos solutions
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 flex items-center gap-6 text-sm text-muted-foreground"
          >
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className="fill-warm text-warm" />
              ))}
            </div>
            <span>4,9/5 · des avis qui transforment votre visibilité locale</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative border border-white/10 bg-surface overflow-hidden">
            <img
              src={PLAQUETTE_IMAGE}
              alt="Chevalet et plaquette avis Google NFC et QR code GMP Développement"
              className="w-full h-[420px] lg:h-[560px] object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 bg-background/60 backdrop-blur-xl border border-white/10 p-4">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-warm">Plaquette Avis Google</p>
              <p className="text-sm mt-1 text-foreground">Posez votre smartphone ou scannez le QR code</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
